import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { href } from 'react-router';
import type { DayAvailability } from '~/ui/availability-calendar';
// The initial fetch below goes out as the page hydrates; see ~/lib/rum-preload.
import '~/lib/rum-preload';

const DEBOUNCE_MS = 300;

const SILENT_RETRY_DELAY_MS = 1000;

function monthKey(base: Date, offset: number): string {
  const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Months we want in the cache when the calendar first opens. Anchored on the
 * `focusDate` (typically the first upcoming event) so that opening the picker
 * and seeing the anchored month fully populated doesn't require an on-demand
 * fetch after the initial focus.
 */
function initialMonths(focusDate: string | null): string[] {
  let base: Date;
  if (focusDate) {
    const [year, month] = focusDate.split('-').map(Number);
    base = year && month ? new Date(year, month - 1, 1) : new Date();
  } else {
    base = new Date();
  }
  return [monthKey(base, 0), monthKey(base, 1), monthKey(base, 2)];
}

type Filters = {
  languages?: string[];
  locationIds?: string[];
  /** Open-hour bookings only: the chosen booking duration in seconds. A day is
   *  only available for the duration the visitor is buying, so this narrows the
   *  day-status map server-side. Fixed events ignore it. */
  duration?: number;
  tickets?: { [variantId: string]: number };
};

function ticketsSignature(tickets?: { [variantId: string]: number }): string {
  if (!tickets) return '';
  return Object.entries(tickets)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => `${id}:${qty}`)
    .sort()
    .join(',');
}

/** Stable stringification so a different ordering of the same filters hits
 *  the same cache entry. */
export function filtersSignature(filters?: Filters): string {
  if (!filters) return '';
  const languages = filters.languages
    ? [...filters.languages].sort().join(',')
    : '';
  const locationIds = filters.locationIds
    ? [...filters.locationIds].sort().join(',')
    : '';
  const duration = filters.duration != null ? String(filters.duration) : '';
  const tickets = ticketsSignature(filters.tickets);
  return `${languages}|${locationIds}|${duration}|${tickets}`;
}

function cacheKey(experienceId: string, filters?: Filters): string {
  const sig = filtersSignature(filters);
  // Always separate with `:` so an experienceId ending in `|` (the
  // signature delimiter) cannot collide with another pair.
  return sig ? `${experienceId}:${sig}` : experienceId;
}

type UseAvailabilityOptions = {
  experienceId: string;
  /** Date (YYYY-MM-DD) to anchor the initial fetch around. Typically the first
   *  upcoming event date — so the calendar opens focused on a fully-loaded
   *  month. */
  initialFocusDate?: string | null;
  /** Optional language / location filters. When provided, both the
   *  per-day availability status and the per-day event lists are filtered
   *  server-side and cached under a separate entry. */
  filters?: Filters;
  /** False when no calendar will be shown (a panel with nothing to book), so
   *  nothing is fetched. Defaults to true. */
  enabled?: boolean;
};

type UseAvailabilityReturn = {
  availability: Record<string, DayAvailability>;
  readyMonths: ReadonlySet<string>;
  /** Months whose availability could not be loaded, even after a quiet retry. */
  failedMonths: ReadonlySet<string>;
  retry: () => void;
  handleMonthChange: (startOfMonth: string) => void;
};

// ---------------------------------------------------------------------------
// Module-level, in-memory client cache.
//
// Keyed by `(experienceId, filtersSignature)` so that switching filters
// doesn't poison entries for other filter combinations. Each entry holds the
// day-status map that drives the calendar dots.
// ---------------------------------------------------------------------------

type CacheEntry = {
  availability: Record<string, DayAvailability>;
  completedMonths: Set<string>;
  failedMonths: Set<string>;
  listeners: Set<() => void>;
};

type MonthResponse = {
  availability: Record<string, DayAvailability>;
};

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<void>>();

function entryFor(key: string): CacheEntry {
  let entry = cache.get(key);
  if (!entry) {
    entry = {
      availability: {},
      completedMonths: new Set(),
      failedMonths: new Set(),
      listeners: new Set(),
    };
    cache.set(key, entry);
  }
  return entry;
}

function notify(entry: CacheEntry) {
  for (const listener of entry.listeners) listener();
}

function buildFetchUrl(
  experienceId: string,
  months: string[],
  filters?: Filters
): string {
  const url = new URL(
    href('/api/v2/availability/:experienceId/:months', {
      experienceId,
      months: months.join(','),
    }),
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin
  );
  for (const lang of filters?.languages ?? []) {
    url.searchParams.append('language', lang);
  }
  for (const loc of filters?.locationIds ?? []) {
    url.searchParams.append('locationId', loc);
  }
  if (filters?.duration != null) {
    url.searchParams.set('duration', String(filters.duration));
  }
  for (const [variantId, qty] of Object.entries(filters?.tickets ?? {})) {
    if (qty > 0) url.searchParams.append('ticket', `${variantId}:${qty}`);
  }
  return url.pathname + url.search;
}

/**
 * One request for the given months, or `null` when it failed. A month that
 * could not be loaded must not look like a month with nothing to book, so a
 * network error or an error answer both count as a failure here.
 */
async function requestMonths(
  experienceId: string,
  months: string[],
  filters?: Filters
): Promise<MonthResponse | null> {
  try {
    const response = await fetch(buildFetchUrl(experienceId, months, filters));
    if (!response.ok) return null;
    return (await response.json()) as MonthResponse;
  } catch {
    return null;
  }
}

async function fetchMonths(
  experienceId: string,
  months: string[],
  filters?: Filters
): Promise<void> {
  const key = cacheKey(experienceId, filters);
  const entry = entryFor(key);
  const needed = months.filter((m) => !entry.completedMonths.has(m));
  if (needed.length === 0) return;

  const dedupeKey = `${key}${needed.join(',')}`;
  const existing = inFlight.get(dedupeKey);
  if (existing) return existing;

  const promise = (async () => {
    try {
      // A new attempt shows the months as loading again, not as failed.
      if (needed.some((m) => entry.failedMonths.has(m))) {
        for (const m of needed) entry.failedMonths.delete(m);
        notify(entry);
      }

      let data = await requestMonths(experienceId, needed, filters);
      if (!data) {
        // A blip should not put a notice in front of the guest.
        await new Promise((resolve) =>
          setTimeout(resolve, SILENT_RETRY_DELAY_MS)
        );
        data = await requestMonths(experienceId, needed, filters);
      }

      if (data) {
        Object.assign(entry.availability, data.availability);
        // An overlapping request may have given up on some of them meanwhile.
        // Left failed, they would keep the notice up over loaded days, and
        // retry() would never ask for them again.
        for (const m of needed) {
          entry.completedMonths.add(m);
          entry.failedMonths.delete(m);
        }
      } else {
        // An overlapping request may have loaded some of them meanwhile.
        for (const m of needed) {
          if (!entry.completedMonths.has(m)) entry.failedMonths.add(m);
        }
      }
      notify(entry);
    } finally {
      inFlight.delete(dedupeKey);
    }
  })();

  inFlight.set(dedupeKey, promise);
  return promise;
}

/**
 * Subscribes the current component to mutations on the availability cache
 * for a given (experienceId, filters) entry. Returns the cache entry; treat
 * the returned objects as read-only snapshots.
 */
function useAvailabilityCache(
  experienceId: string,
  filters?: Filters
): CacheEntry {
  const key = cacheKey(experienceId, filters);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const entry = entryFor(key);
    entry.listeners.add(forceUpdate);
    return () => {
      entry.listeners.delete(forceUpdate);
    };
  }, [key]);
  return entryFor(key);
}

/**
 * Hook that manages progressive availability loading for the calendar.
 * Returns the calendar dot-status map, the sets of fetched and failed months,
 * a retry for the failed ones, and a debounced month-change handler that warms
 * neighbouring months on navigation.
 */
export function useAvailability({
  experienceId,
  initialFocusDate,
  filters,
  enabled = true,
}: UseAvailabilityOptions): UseAvailabilityReturn {
  // Memoise the filter signature so a fresh `filters` object on every
  // render doesn't bust the effect.
  const filterSig = filtersSignature(filters);
  const memoFilters = useMemo(() => filters, [filterSig]); // eslint-disable-line
  const entry = useAvailabilityCache(experienceId, memoFilters);

  const todayMonth = monthKey(new Date(), 0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load — no-op if the cache is already warm (from prefetch or a
  // prior open of the date picker).
  useEffect(() => {
    if (!enabled) return;
    void fetchMonths(
      experienceId,
      initialMonths(initialFocusDate ?? null),
      memoFilters
    );
  }, [enabled, experienceId, initialFocusDate, memoFilters]);

  const handleMonthChange = useCallback(
    (startOfMonth: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        const [year, month] = startOfMonth.split('-').map(Number);
        if (!year || !month) return;
        const base = new Date(year, month - 1, 1);
        const months = [
          monthKey(base, -1),
          monthKey(base, 0),
          monthKey(base, 1),
        ].filter((m) => m >= todayMonth);
        void fetchMonths(experienceId, months, memoFilters);
      }, DEBOUNCE_MS);
    },
    [experienceId, todayMonth, memoFilters]
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const retry = useCallback(() => {
    void fetchMonths(experienceId, [...entry.failedMonths], memoFilters);
  }, [entry, experienceId, memoFilters]);

  return {
    availability: entry.availability,
    readyMonths: entry.completedMonths,
    failedMonths: entry.failedMonths,
    retry,
    handleMonthChange,
  };
}

type PrefetchAvailabilityProps = {
  experienceId: string;
  /** Date to anchor the prefetch around. Typically the first upcoming event
   *  date, so the warmed months match what the calendar will open on. */
  initialFocusDate?: string | null;
};

/**
 * Renders nothing. Warms the in-memory availability cache on idle time so
 * the calendar appears instantly the first time the user opens the date
 * picker.
 *
 * Prefetches the filter-free entry only — checkout's existing call sites
 * don't pass filters today, so this matches what they'll consume. If a
 * future caller needs a filter-aware prewarm, accept `filters` here and
 * forward to `fetchMonths`.
 */
export function PrefetchAvailability({
  experienceId,
  initialFocusDate,
}: PrefetchAvailabilityProps) {
  useEffect(() => {
    const doPrefetch = () => {
      void fetchMonths(experienceId, initialMonths(initialFocusDate ?? null));
    };

    if (
      typeof window !== 'undefined' &&
      typeof window.requestIdleCallback === 'function'
    ) {
      const id = window.requestIdleCallback(doPrefetch, { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(doPrefetch, 1500);
    return () => clearTimeout(id);
  }, [experienceId, initialFocusDate]);

  return null;
}
